const id = (value) => value?.id ?? value;
const same = (a, b) => a != null && b != null && String(id(a)) === String(id(b));

const BOOKED_STATUSES = ['BOOKED', 'CONSULTATION_BOOKED'];
const INACTIVE_STATUSES = ['ARCHIVED', 'CANCELLED'];
const isActionable = (item) => !item?.is_archived && !INACTIVE_STATUSES.includes(String(item?.status || '').toUpperCase());

// Shared lookup: validates the row/lead/practice and returns the treatment plus
// every appointment of THAT treatment (never another treatment's).
function findTreatmentAppointments(practiceId, row, lead, explicit) {
  const leadId = explicit?.leadId ?? row?.lead_id ?? lead?.id;
  // Treatment: explicit, else the row's, else the row's matched treatment, else the
  // lead's only CONSULTATION_BOOKED treatment (lead-detail entry has no row treatment).
  const booked = (lead?.lead_treatments || []).filter(item => item?.status === 'CONSULTATION_BOOKED');
  const treatmentId = explicit?.leadTreatmentId ?? row?.treatment_id ?? row?.matched_treatment_id
    ?? (booked.length === 1 ? booked[0].id : null);
  if (!practiceId || !leadId || !treatmentId || !same(lead?.id, leadId)
    || (explicit?.practiceId && !same(explicit.practiceId, practiceId))) return null;
  const treatment = lead?.lead_treatments?.find((item) => same(item.id, treatmentId));
  if (!treatment) return null;
  const topLevel = (lead.lead_appointment || []).filter(item => same(item.lead_treatment, treatmentId));
  const nested = (treatment.appointments || []).filter(item => !item.lead_treatment || same(item.lead_treatment, treatmentId));
  const candidates = [...topLevel, ...nested.filter(item => !topLevel.some(other => same(other.id, item.id)))];
  return { leadId, treatment, candidates, appointmentId: explicit?.appointmentId ?? row?.appointment_id ?? row?.appointment?.id };
}

// No explicit appointment id: drop archived/cancelled appointments, then prefer
// the booked ones (this is the outcome-recording flow). Never picks between peers.
const narrowCandidates = (candidates) => {
  const actionable = candidates.filter(isActionable);
  const booked = actionable.filter(item => BOOKED_STATUSES.includes(item.status));
  return booked.length ? booked : actionable;
};

// Appointments the user must choose between when the row does not identify one.
// Empty unless the selection is genuinely ambiguous (>1 actionable candidates).
export function listAmbiguousAppointmentCandidates(practiceId, row, lead, explicit = null) {
  const found = findTreatmentAppointments(practiceId, row, lead, explicit);
  if (!found || found.appointmentId) return [];
  const narrowed = narrowCandidates(found.candidates);
  return narrowed.length > 1 ? narrowed : [];
}

// A selected row is authoritative. A unique actionable appointment in that
// treatment may fill a missing row ID; ambiguity is never resolved using another
// treatment, and never guessed (null -> caller offers a chooser).
export function resolveAppointmentOutcomeSelection(practiceId, row, lead, explicit = null) {
  const found = findTreatmentAppointments(practiceId, row, lead, explicit);
  if (!found) return null;
  const { leadId, treatment, candidates, appointmentId } = found;
  let appointment = null;
  if (appointmentId) appointment = candidates.find(item => same(item.id, appointmentId));
  else {
    const narrowed = narrowCandidates(candidates);
    appointment = narrowed.length === 1 ? narrowed[0] : null;
  }
  if (!appointment) return null;
  return { practiceId, leadId, leadTreatmentId: treatment.id, appointmentId: appointment.id, appointment, treatment,
    calcomDerived: Boolean(appointment.calcom || (appointment.contacted_via === 'ONLINE_BOOKING'
      && String(lead.lead_source?.referral || '').toUpperCase() === 'CALCOM')) };
}
